import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, TrendingUp, Users, Award, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const LeaderboardsPage = () => {
  const { API } = useContext(AuthContext);
  const [leaderboards, setLeaderboards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('points');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const skillCategories = [
    { value: 'all', label: 'כל הדרגות' },
    { value: 'דרג א', label: 'דרג א' },
    { value: 'דרג ב', label: 'דרג ב' },
    { value: 'דרג ג', label: 'דרג ג' },
    { value: 'יחידים open', label: 'יחידים Open' },
    { value: 'זוגות open', label: 'זוגות Open' },
    { value: 'נשים', label: 'נשים' },
  ];

  const tabs = [
    { id: 'points', label: 'נקודות', icon: Trophy, dataKey: 'top_points', valueKey: 'total_points', valueLabel: 'נק\'' },
    { id: 'wins', label: 'ניצחונות', icon: Award, dataKey: 'top_wins', valueKey: 'total_wins', valueLabel: 'ניצ\'' },
    { id: 'tournaments', label: 'תחרויות', icon: Users, dataKey: 'top_tournaments', valueKey: 'total_tournaments', valueLabel: 'תחרויות' },
    { id: 'win_rate', label: 'אחוז ניצחון', icon: TrendingUp, dataKey: 'top_win_rate', valueKey: 'win_rate', valueLabel: '%', isPercentage: true },
    { id: 'streaks', label: 'רצף ניצחונות', icon: Flame, dataKey: 'winning_streaks', valueKey: 'winning_streak', valueLabel: 'רצף' },
  ];

  useEffect(() => {
    fetchLeaderboards();
  }, [selectedCategory]);

  const fetchLeaderboards = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? { skill_level: selectedCategory } : {};
      const response = await axios.get(`${API}/leaderboards`, { params });
      setLeaderboards(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת לוחות מובילים');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.substring(0, 2);
  };

  const activeTabConfig = tabs.find(t => t.id === activeTab);
  const currentData = leaderboards?.[activeTabConfig?.dataKey] || [];

  const TopThreeHero = () => {
    const top3 = currentData.slice(0, 3);
    if (top3.length < 3) return null;

    const podiumConfig = [
      { player: top3[1], rank: 2, height: 'h-24 sm:h-28', avatarSize: 'h-16 w-16 sm:h-20 sm:w-20', ring: 'ring-slate-300', medal: 'from-slate-300 to-slate-400', bg: 'from-slate-400 to-slate-300' },
      { player: top3[0], rank: 1, height: 'h-32 sm:h-36', avatarSize: 'h-20 w-20 sm:h-24 sm:w-24', ring: 'ring-yellow-400', medal: 'from-yellow-400 to-amber-500', bg: 'from-amber-500 to-yellow-400' },
      { player: top3[2], rank: 3, height: 'h-20 sm:h-24', avatarSize: 'h-14 w-14 sm:h-18 sm:w-18', ring: 'ring-amber-600', medal: 'from-amber-600 to-orange-700', bg: 'from-orange-700 to-amber-600' },
    ];

    const valueKey = activeTabConfig?.valueKey;
    const isPercentage = activeTabConfig?.isPercentage;

    return (
      <div className="mb-12" data-testid="leaderboard-podium">
        <div className="flex justify-center items-end gap-2 sm:gap-5">
          {podiumConfig.map(({ player, rank, height, avatarSize, ring, medal, bg }) => {
            const val = player[valueKey] || 0;
            const displayVal = isPercentage ? `${Math.round(val * 100)}%` : val;
            return (
              <Link 
                key={player.id} 
                to={`/profile/${player.id}`}
                className="group flex flex-col items-center"
                data-testid={`leaderboard-podium-${rank}`}
              >
                {rank === 1 && (
                  <div className="mb-1">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="drop-shadow">
                      <path d="M12 2L14.5 8.5L21 9.5L16 14L17.5 21L12 17.5L6.5 21L8 14L3 9.5L9.5 8.5L12 2Z" fill="#FBBF24" stroke="#F59E0B" strokeWidth="1"/>
                    </svg>
                  </div>
                )}
                <div className={`relative mb-1 ${rank === 1 ? 'scale-105' : ''} transition-transform group-hover:scale-105`}>
                  <div className={`ring-3 ${ring} rounded-full shadow-lg`}>
                    <Avatar className={`${avatarSize} shadow-md`}>
                      <AvatarImage src={player.avatar} alt={player.full_name} />
                      <AvatarFallback className={`bg-gradient-to-br ${medal} text-white font-bold text-base`}>
                        {getInitials(player.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gradient-to-br ${medal} flex items-center justify-center text-white font-bold text-xs shadow border-2 border-white`}>
                    {rank}
                  </div>
                </div>
                <p className="text-center font-bold text-gray-800 text-xs sm:text-sm max-w-[80px] sm:max-w-[110px] truncate mt-1 group-hover:text-teal-600 transition-colors">{player.full_name}</p>
                <p className="text-[10px] sm:text-xs text-gray-500 mb-1.5">{displayVal} {!isPercentage && activeTabConfig?.valueLabel}</p>
                <div className={`${height} w-16 sm:w-24 bg-gradient-to-t ${bg} rounded-t-lg flex flex-col items-center justify-start pt-2 shadow-md relative overflow-hidden`}>
                  <span className="text-white/90 font-black text-2xl">{rank}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>
              </Link>
            );
          })}
        </div>
        <div className="max-w-xs mx-auto h-1.5 bg-gradient-to-r from-slate-300 via-yellow-400 to-amber-600 rounded-b-lg" />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-3" data-testid="leaderboards-title">
            לוח מובילים
          </h1>
          <p className="text-base text-gray-500">
            השחקנים המובילים בקהילת הטניס
          </p>
        </div>

        {/* Category filter */}
        <div className="mb-6" data-testid="skill-category-filter">
          <div className="flex flex-wrap justify-center gap-2">
            {skillCategories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                data-testid={`skill-filter-${cat.value}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200
                  ${selectedCategory === cat.value
                    ? 'bg-gray-900 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stat Tabs */}
        <div className="mb-8" data-testid="stat-tabs">
          <div className="flex justify-center gap-1 bg-gray-100 rounded-xl p-1 max-w-2xl mx-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 flex-1
                    ${activeTab === tab.id
                      ? 'bg-white text-gray-900 shadow-md'
                      : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="py-16">
            <LoadingSpinner size="lg" text="טוען מובילים..." />
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            <TopThreeHero />

            {/* Leaderboard List */}
            <Card className="overflow-hidden shadow-xl border-0 bg-white" data-testid="leaderboard-list">
              <CardHeader className="bg-gray-900 text-white py-4 px-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 font-bold">
                    {activeTabConfig && <activeTabConfig.icon className="w-5 h-5" />}
                    {activeTabConfig?.label ? `מובילים לפי ${activeTabConfig.label}` : 'מובילים'}
                  </CardTitle>
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">
                    {currentData.length} שחקנים
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {currentData.length > 0 ? (
                  <div className="divide-y divide-gray-50">
                    {currentData.map((player, index) => {
                      const val = player[activeTabConfig?.valueKey] || 0;
                      const displayVal = activeTabConfig?.isPercentage ? `${Math.round(val * 100)}%` : val;
                      const maxVal = currentData[0]?.[activeTabConfig?.valueKey] || 1;
                      const barWidth = activeTabConfig?.isPercentage 
                        ? Math.round(val * 100) 
                        : maxVal > 0 ? Math.round((val / maxVal) * 100) : 0;
                      
                      return (
                        <div 
                          key={player.id}
                          className="flex items-center px-5 py-3 hover:bg-teal-50/40 transition-colors group"
                          data-testid={`leaderboard-row-${index}`}
                        >
                          {/* Rank */}
                          <div className="w-10 shrink-0">
                            {index < 3 ? (
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs
                                ${index === 0 ? 'bg-yellow-400 text-yellow-900' : ''}
                                ${index === 1 ? 'bg-slate-300 text-slate-700' : ''}
                                ${index === 2 ? 'bg-amber-600 text-white' : ''}
                              `}>
                                {index + 1}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm font-medium">{index + 1}</span>
                            )}
                          </div>

                          {/* Player */}
                          <Link to={`/profile/${player.id}`} className="flex items-center gap-3 min-w-[160px] group/link">
                            <Avatar className="h-9 w-9 ring-1 ring-gray-100 shadow-sm">
                              <AvatarImage src={player.avatar} alt={player.full_name} />
                              <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white font-bold text-xs">
                                {getInitials(player.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-semibold text-sm text-gray-800 group-hover/link:text-teal-600 transition-colors truncate max-w-[120px]">
                              {player.full_name}
                            </span>
                          </Link>

                          {/* Bar + Value */}
                          <div className="flex-1 flex items-center gap-3 mr-4">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full transition-all duration-500"
                                style={{ width: `${Math.max(barWidth, 2)}%` }}
                              />
                            </div>
                            <span className="font-bold text-sm text-gray-900 min-w-[50px] text-left">
                              {displayVal}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-lg text-gray-500">אין נתונים להצגה</p>
                    <p className="text-gray-400 mt-1 text-sm">נסה קטגוריה אחרת</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardsPage;
